import React, { useEffect, useMemo } from "react";
import PageTitleActions from "./PageTitleActions";
import { useConfig } from "../../../DataStore/ConfigContext";
import { useTheme } from "../../../DataStore/ThemeContext";

const PageTitle = ({
  title,
  description,
  showsCompany = false,
  iconClass = null,
  PageActions = null,
  sticky = true,
  Pagination = null,
}) => {
  const { company, currentPeriod, getCompanyProfile, getCurrentPeriod } = useConfig();
  const { currentNotify } = useTheme();

  useEffect(() => {
    if (showsCompany) {
      getCompanyProfile();
      getCurrentPeriod();
    }
  }, [showsCompany]);

  const fyText = useMemo(() => {
    if (!currentPeriod) return "...";
    const o = currentPeriod?.opening_date?.short_date || currentPeriod?.opening_date || "";
    const c = currentPeriod?.closing_date?.short_date || currentPeriod?.closing_date || "";
    return `${o} - ${c}`;
  }, [currentPeriod]);

  const companyName = useMemo(() => company?.client_name || "...", [company]);

  return (
    <>
      <style>{`
        .yx-page-title-wrap{
          border-radius: 18px;
          border: 1px solid rgba(15,23,42,.06);
          background: #fff;
          box-shadow: 0 1px 2px rgba(16,24,40,.04);
        }

        /* Sticky wrapper (works when parents allow sticky) */
        .yx-page-title-sticky{
          position: sticky;
          top: 52px; /* adjust if your top navbar height differs */
          z-index: 10;
        }

        .yx-page-title-head{
          padding: 16px 20px;
          background: linear-gradient(180deg, rgba(79,70,229,.06), rgba(255,255,255,0));
          border-bottom: 1px solid rgba(15,23,42,.06);
        }

        .yx-title-left{
          display:flex;
          align-items:center;
          gap: 14px;
          min-width: 0;
        }

        .yx-title-ic{
          width: 52px;
          height: 52px;
          border-radius: 16px;
          display:flex;
          align-items:center;
          justify-content:center;
          background: #4F46E5;
          color: #fff;
          box-shadow: 0 10px 22px rgba(79,70,229,.18);
          flex: 0 0 auto;
        }

        .yx-title-ic i{ font-size: 22px; opacity: .95; }

        .yx-title-h{
          margin: 0;
          font-weight: 900;
          font-size: 20px;
          color: #0F172A;
          letter-spacing: .2px;
          line-height: 1.2;
        }

        .yx-title-sub{
          margin-top: 4px;
          color: rgba(71,85,105,.9);
          font-size: 13px;
          line-height: 1.35;
        }

        .yx-title-actions{
          display:flex;
          align-items:center;
          gap: 10px;
        }

        /* ✅ single-line meta chip (no extra height on desktop) */
        .yx-metaInline{
          display:flex;
          align-items:center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 14px;
          border: 1px solid rgba(79,70,229,.12);
          background: rgba(79,70,229,.05);
          max-width: 420px;
          min-width: 280px;
        }

        .yx-miIcon{
          width: 34px;
          height: 34px;
          border-radius: 12px;
          display:flex;
          align-items:center;
          justify-content:center;
          background: rgba(79,70,229,.10);
          color: #4F46E5;
          flex: 0 0 auto;
        }

        .yx-miText{
          min-width: 0;
          display:block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 13.5px;
          line-height: 1;
          font-weight: 900;
          color: rgba(15,23,42,.88);
        }

        .yx-miCompany{ font-weight: 900; }

        .yx-miSep{
          margin: 0 8px;
          color: rgba(71,85,105,.55);
          font-weight: 900;
        }

        .yx-miLabel{
          color: rgba(71,85,105,.75);
          font-weight: 900;
          margin-right: 6px;
        }

        .yx-miFy{
          color: #4F46E5;
          font-weight: 900;
        }

        @media (max-width: 1200px){
          .yx-metaInline{ max-width: 340px; min-width: 240px; }
        }

        @media (max-width: 992px){
          .yx-metaInline{ width: 100%; max-width: 100%; min-width: 100%; }
        }

        @media (max-width: 768px){
          .yx-page-title-head{ padding: 16px; }
          .yx-title-ic{ width: 46px; height: 46px; border-radius: 14px; }
          .yx-title-h{ font-size: 18px; }
        }
      `}</style>

      <div className={`mb-4 ${sticky ? "yx-page-title-sticky" : ""}`}>
        <div className="yx-page-title-wrap">
          <div className="yx-page-title-head">
            <div className="d-flex align-items-start justify-content-between flex-wrap">
              {/* Left */}
              <div className="yx-title-left">
                {iconClass && (
                  <div className="yx-title-ic">
                    <i className={iconClass} />
                  </div>
                )}

                <div style={{ minWidth: 0 }}>
                  <h2 className="yx-title-h">{title}</h2>
                  {description ? <div className="yx-title-sub">{description}</div> : null}
                </div>
              </div>

              {/* Right */}
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
                {/* Pagination + Actions */}
                <div className="yx-title-actions">
                  {Pagination ? <div className="mr-2">{Pagination}</div> : null}
                  {PageActions ? <PageTitleActions DropdownItems={PageActions} /> : null}
                </div>

                {/* ✅ Company + FY (single line, no extra height on desktop) */}
                {showsCompany ? (
                  <div
                    className="yx-metaInline d-none d-lg-flex"
                    title={`${companyName} • FY: ${fyText}`}
                  >
                    <span className="yx-miIcon">
                      <i className="fa fa-university" />
                    </span>

                    <span className="yx-miText">
                      <span className="yx-miCompany">{companyName}</span>
                      <span className="yx-miSep">•</span>
                      <span className="yx-miLabel">FY:</span>
                      <span className="yx-miFy">{fyText}</span>
                    </span>
                  </div>
                ) : null}
              </div>

              {/* ✅ Mobile/Tablet company block (full width, still single-line, truncates) */}
              {showsCompany ? (
                <div className="yx-metaInline d-flex d-lg-none mt-3 w-100" title={`${companyName} • FY: ${fyText}`}>
                  <span className="yx-miIcon">
                    <i className="fa fa-university" />
                  </span>

                  <span className="yx-miText">
                    <span className="yx-miCompany">{companyName}</span>
                    <span className="yx-miSep">•</span>
                    <span className="yx-miLabel">FY:</span>
                    <span className="yx-miFy">{fyText}</span>
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {currentNotify}
    </>
  );
};

export default PageTitle;
